(ns dre.widgets.clinical-studies.events
  (:require
   [applied-science.js-interop :as j]
   [dre.fx :as-alias dre.fx]
   [clojure.string :as str]
   [day8.re-frame.tracing :refer-macros [fn-traced]]
   [dre.i18n.views :refer [tr]]
   [dre.i18n.utils :as i18n-utils]
   [dre.network :as-alias network]
   [dre.pages.search.events :as search-events]
   [dre.page-util :refer [capitalize-words]]
   [dre.utils :refer [filter-map tag-name stringify-keys-with-namespaces aborted-request?]]
   [dre.shared.interface.names :as names :refer [pubmed-pubtype curated-tag-labels]]
   [dre.shared.interface.search.pills :as pills]
   [re-frame.core :as rf]
   [superstructor.re-frame.fetch-fx :as-alias fetch-fx]))

;; extension of analysis/tag_distribution

(def db-path [:widgets :clinical-studies])

(def id
  :clinical-studies)

(def exclude-ids
  [:clinical-studies-results
   :clinical-studies-default
   :clinical-studies-size
   :clinical-studies-study
   :clinical-studies-phase])

(def default-study-size-interval
  100)

(defn data-filters
  [db]
  (let [keys (filter #(and % (str/starts-with? (name %) (name id)))
                     (->> db
                          search-events/db-path
                          :widget-filters keys))]
    (mapcat
     #(->> db
           search-events/db-path
           :widget-filters
           %) keys)))

(defn- study-pill?
  [{:pill/keys [type query]}]
  (and (= type :tag)
       (or (= (str/lower-case (name query)) "predicted/d016430")
           (= (str/lower-case (name query)) "predicted/d016449")
           (str/starts-with? query "phase/")
           (str/starts-with? query "study/"))))

(defn data-queries-study
  [db]
  (->> db
       search-events/db-path
       :search-query
       :query/pills
       (filter study-pill?)))

(defn- size-pill?
  [{:pill/keys [type query]}]
  (= type :study-size))

(defn data-queries-size
  [db]
  (->> db
       search-events/db-path
       :search-query
       :query/pills
       (filter size-pill?)))

(defn selected-data?
  [db]
  (let [x (concat (data-queries-study db) (data-queries-size db) (data-filters db))]
    (not-empty x)))

;; tag distribution
(defn ->pill
  [term]
  (let [label (or (get curated-tag-labels (str/lower-case (name term)))
                  (when (= "study" (namespace term))
                    (capitalize-words (name term)))
                  (if (and (= (namespace term) "predicted") (= "D016449" (str/upper-case (name term))))
                    (get pubmed-pubtype (str/upper-case (str "PREDICTED-" (name term))))
                    (get pubmed-pubtype (str/upper-case (name term)))))]
    {:pill/type :tag
     :pill/query (tag-name term)
     :pill/display-label label
     :pill/label (if (and label (= (namespace term) "pubtype"))
                   (str "PubMed " label) label)}))

(defn prepare-study-results-data
  [data filters key filter-key]
  (let [data (filter-map data #(#{:study/with-results
                                  :study/without-results} %))
        filter-key (keyword (str (name filter-key) "-results"))
        f (get filters filter-key)
        click (fn [filter-key term]
                #(rf/dispatch [::search-events/widget-filter-select
                               filter-key (->pill term)]))]
    (map (fn [[term count]]
           (let [pill (->pill term)
                 f? (contains? f pill)
                 label (:pill/label pill)]
             (hash-map
              :label label
              :display-label (:pill/display-label pill)
              :term term
              :class [(when (seq f) (if f? "active" "inactive"))]
              :on-click (click filter-key term)
              :count count)))
         data)))

;; study size

(defn- normalize-key
  "Converts symbols to integers."
  [[k v]]
  [(-> k
       name
       js/parseInt)
   v])

(defn- ->label
  "Interpolates table row label for study size result."
  [start end]
  (str start " - " end " participants"))

(defn- study-size->table-row
  "Generates table row data from study size result."
  [filters interval filter-key [start doc-count]]
  (let [f (get filters filter-key)
        end (dec (+ start interval))
        pill (pills/study-size-pill (str start) (str end))]
    {:label (->label start end)
     :count doc-count
     :class (when (seq f)
              (if (contains? f pill)
                :active
                :inactive))
     :on-click #(rf/dispatch [::search-events/widget-filter-select
                              filter-key
                              pill])}))

;; categories

(defn ->category-pill [node]
  (let [{:keys [type name]} node
        ct-query "predicted/d016430"
        rct-query "predicted/d016449"
        query (cond
                (= type "ct")
                ct-query
                (= type "rct")
                rct-query
                :else
                type)
        study-design? (or (= type "ct") (= type "rct"))]
    (if study-design?
      {:pill/type :and
       :pill/pills [{:pill/type :or
                     :pill/pills [{:pill/type :category
                                   :pill/query "who-ictrp"
                                   :pill/label "WHO ICTRP"}
                                  {:pill/type :category
                                   :pill/query "clinicaltrials"
                                   :pill/label "ClinicalTrials.gov"}]}
                    {:pill/type :tag
                     :pill/query query
                     :pill/label name}]}
      {:pill/type :tag
       :pill/query query
       :pill/label name})))


(defn ->category-pills [path]
  (if (= (count path) 1)
    (->category-pill (first path))
    {:pill/type :and
     :pill/pills (mapv ->category-pill path)}))

(defn recruitment-buckets
  [doc_count by_recruitment_status]
  (let [other-count (- doc_count
                       (reduce + (map :doc_count (get by_recruitment_status :buckets))))]
    (-> []
        (cond->
         (pos? other-count)
          (conj {:name "Other"
                 :type "other"
                 :level "Recruitment status"
                 :count other-count}))
        (into (map (fn [{:keys [key doc_count]}]
                     {:name key
                      :type key
                      :level "Recruitment status"
                      :count doc_count}))
              (get by_recruitment_status :buckets)))))

(defn phase-buckets
  [doc_count by_phase by_recruitement_status_not_phase]
  ;; by_recruitement_status_not_phase are buckets that are ct > rct > status or ct > status (without a phase in between)
  (let [by_recruitement_status_not_phase_aggs (get by_recruitement_status_not_phase :by_recruitment_status)
        phase-count (reduce + (map :doc_count (get by_phase :buckets)))
        not-phase-count (reduce + (map :doc_count (get by_recruitement_status_not_phase_aggs :buckets)))
        other-count (- doc_count
                       (+ phase-count
                          not-phase-count))]
    (-> []
        (cond->
         (pos? other-count)
          (conj {:name "Other"
                 :type "other"
                 :level "Phase"
                 :count other-count}))
        ;; phase buckets
        (into
         (map (fn [{:keys [key doc_count by_recruitment_status]}]
                {:name key
                 :type key
                 :level "Phase"
                 :count doc_count
                 :children (recruitment-buckets doc_count by_recruitment_status)}))
         (get by_phase :buckets))
        ;; recruitement-buckets without phase
        (into
         (map (fn [{:keys [key doc_count]}]
                {:name key
                 :type key
                 :level "Recruitment status"
                 :count doc_count}))
         (get by_recruitement_status_not_phase_aggs :buckets)))))

(defn pubtype-buckets
  [doc_count by_pubtype_rct by_pubtype_ct_not_rct]
  ;; to get the right numbers for the subcatories by phase the rct and ct_not_rct are disjoint sets
  ;; in the chart rct is shown as a subcategory of ct
  (let [by_pubtype_rct (first (:buckets by_pubtype_rct))
        other-count (- doc_count
                       (+ (:doc_count by_pubtype_rct) (:doc_count by_pubtype_ct_not_rct)))]
    (-> []
        (cond->
         (pos? other-count)
          (conj {:name "Other"
                 :type "other"
                 :level "Pubtype"
                 :count other-count}))
        (conj (let [{:keys [doc_count by_phase by_recruitement_status_not_phase]} by_pubtype_ct_not_rct
                    rct_doc_count (get by_pubtype_rct :doc_count 0)]
                {:name "Clinical Study"
                 :type "ct"
                 :level "Pubtype"
                 :count (+ doc_count rct_doc_count)
                 :children (into [{:name "Randomized Clinical Trial"
                                   :type "rct"
                                   :level "PubType"
                                   :count rct_doc_count
                                   :children (phase-buckets rct_doc_count
                                                            (:by_phase by_pubtype_rct)
                                                            (:by_recruitement_status_not_phase by_pubtype_rct))}]
                                 (phase-buckets doc_count
                                                by_phase
                                                by_recruitement_status_not_phase))})))))

(defn category-buckets
  [buckets]
  (mapv (fn [{:keys [key doc_count by_pubtype_rct by_pubtype_ct_not_rct]}]
          {:name key
           :type key
           :level "Category"
           :count doc_count
           :children (pubtype-buckets doc_count by_pubtype_rct by_pubtype_ct_not_rct)})
        buckets))

(defn str-name
  [name]
  (let [name (get names/categories name name)
        [head rest] (str/split name "/")]
    (if rest (str/capitalize rest) head)))

(defn to-sankey [table]
  (let [collect (fn collect [node nodes-arr links-arr]
                  (let [_ (doseq [bucket (:children node)
                                  :let [bucket-name (str-name (:name bucket))]]
                            (when (not (some (fn [node]
                                               (= (.-name node) bucket-name))
                                             nodes-arr))
                              (.push nodes-arr #js {:name bucket-name
                                                    :type (:type bucket)}))
                            (doseq [child (:children bucket)
                                    :let [child-name (str-name (:name child))]
                                    :when (< 0 (:count child))]
                              (if-let [existing-link (some
                                                      (fn [link]
                                                        (when (and (= (.-source link) bucket-name)
                                                                   (= (.-target link) child-name))
                                                          link))
                                                      links-arr)]
                                ;; update link
                                (j/assoc! existing-link :value (+ (j/get existing-link :value)
                                                                  (:count child)))
                                ;; create link
                                (.push links-arr #js {:source bucket-name
                                                      :target child-name
                                                      :sourceType (:type bucket)
                                                      :targetType (:type child)
                                                      :names #js [bucket-name child-name]
                                                      :value (:count child)})))
                            (collect bucket nodes-arr links-arr))]
                    [nodes-arr links-arr]))

        ;; top level category of source/category not shown
        ;; root should be "Clinical Study", which is the combination of ct from the "All" category
        table (->> (reduce
                    (fn [m category]
                      (let [cat-name (str-name (:name category))]
                        (if (get m cat-name)
                          (update m cat-name
                                  (fn [bucket]
                                    (-> bucket
                                        (update :count + (:count category))
                                        (update :children into (:children category)))))
                          (assoc m cat-name category))))
                    {}
                    (mapcat :children (:children table)))
                   vals
                   (assoc {} :children))
        [nodes-in links-in] (collect table #js [] #js [])]
    (if (zero? (count links-in))
      nil ;; no results
      #js {:nodes nodes-in
           :links links-in})))

(defn categories->rows
  [categories]
  (let [name->type (->> categories
                        :nodes
                        (mapcat (juxt :name :type))
                        (apply hash-map))
        links (:links categories)
        sources (group-by :source links)
        sources (for [[k v] sources
                      :let [value (reduce + (map :value v))
                            type (name->type k)]]
                  [k value type])
        targets (group-by :target links)
        targets (for [[k v] targets
                      :let [value (reduce + (map :value v))
                            type (name->type k)]]
                  [k value type])
        data (into [] (distinct) (concat sources targets))
        data (sort-by second > data)]
    data))


(defn- trial-phase?
  [[_ _ type]]
  (str/starts-with? type "phase/"))

(defn- trial-status?
  [[_ _ type]]
  (str/starts-with? type "study/"))

(defn- rows->groups
  [rows]
  (group-by #(cond
               (trial-phase? %)  :trial-phase
               (trial-status? %) :trial-status
               :else             :study-design)
            rows))

(defn- normalize-groups
  [{:keys [study-design trial-phase trial-status]}]
  (concat [[[tr "Study Design"] nil nil]]
          study-design
          [[[tr "Trial Phase"] nil nil]]
          trial-phase
          [[[tr "Trial Status"] nil nil]]
          trial-status))

(defn- ->table-data
  [data filters filter-key]
  (->> data
       (map (fn [[label count type]]
              (if (and (nil? count)
                       (nil? type))
                {:label [:span.separator label]
                 :term "divider"
                 :asis true
                 :style {:font-weight "bold"}}
                (let [pill (->category-pill {:type type
                                             :name label})
                      prefix (or (namespace (keyword type)) "default")
                      filter-key (keyword (str (name filter-key) "-" prefix))
                      f (get filters filter-key)]
                  (hash-map :label label
                            :count count
                            :class (if (= label "Other")
                                     :inactive
                                     (when (seq f)
                                       (if (contains? f pill)
                                         :active
                                         :inactive)))
                            :on-click #(when (not= label "Other")
                                         (rf/dispatch
                                          [::search-events/widget-filter-select
                                           filter-key
                                           pill])))))))))

;; -- Events

(rf/reg-event-fx
 ::set-loading
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} _]
  {:db (assoc db :state {:tags :loading
                         :study-size :loading
                         :categories :loading})}))

(rf/reg-event-fx
 ::init
 [(rf/path db-path) rf/trim-v]
 (fn
  [{db :db} [{:keys [after-fx last-selected-filter-key pills-api-params-fn]}]]
  (let [params (pills-api-params-fn exclude-ids)
        study-size-int (or (get db :study-size-interval) default-study-size-interval)]
    (if (and last-selected-filter-key
             (str/starts-with? (name last-selected-filter-key) (name id)))
     {:db (assoc db :state {:tags nil
                            :study-size nil
                            :categories nil})
      :dispatch after-fx}
     {:db {:state {:tags :loading
                   :study-size :loading
                   :categories :loading}
           :data-study-results nil
           :data-study-size nil
           :study-size-interval study-size-int}
      :fx [[::network/request-with-abort [{:endpoint :widgets/tag-distribution}
                                          {:body (stringify-keys-with-namespaces (assoc params :response/filter {:distribution true}))
                                           :on-success [::success-tag-distribution {:after-fx after-fx}]
                                           :on-failure [::failure-tag-distribution {:after-fx after-fx}]}]]
           [::network/request-with-abort [{:endpoint :widgets/study-size}
                                          {:body (stringify-keys-with-namespaces (assoc params :interval study-size-int))
                                           :on-success [::success-study-size {:after-fx after-fx}]
                                           :on-failure [::failure-study-size {:after-fx after-fx}]}]]
           [::network/request-with-abort [{:endpoint :widgets/article-categories}
                                          {:body (stringify-keys-with-namespaces params)
                                           :on-success [::success-categories {:after-fx after-fx}]
                                           :on-failure [::failure-categories {:after-fx after-fx}]}]]]}))))

(rf/reg-event-fx
 ::success-categories
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [after-fx ::fetch-fx/response]}]]
  (let [table (->> (get-in (:body response) [:aggs :buckets])
                   (filter #(#{"who-ictrp" "clinicaltrials"} (:key %)))
                   category-buckets)
        root {:name "All"
              :type "root"
              :level "All"
              :count (reduce + (map :count table))
              :children table}
        js-results (to-sankey root)]
    {:db (-> db
             (assoc-in [:state :categories] :success)
             (assoc-in [:data-categories] (js->clj js-results :keywordize-keys true))
             (assoc-in [:data-categories-sankey] js-results))
   :dispatch after-fx})))

(rf/reg-event-fx
 ::failure-categories
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [after-fx ::fetch-fx/response] :as opts}]]
  (if (aborted-request? response)
    {}
    {:db (-> db
             (assoc-in [:state :categories] :failure)
             (assoc-in [:data-categories] nil))
     :dispatch-n [after-fx
                  [:dre.network/default-failure (merge {:event :clinical-studies-categories} opts)]]})))

(rf/reg-event-fx
 ::success-tag-distribution
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [after-fx ::fetch-fx/response]}]]
  {:db (-> db
           (assoc-in [:state :tags] :success)
           (assoc-in [:data-study-results] (:distribution (:body response))))
   :dispatch after-fx}))

(rf/reg-event-fx
 ::failure-tag-distribution
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [after-fx ::fetch-fx/response] :as opts}]]
  (if (aborted-request? response)
    {}
    {:db (-> db
             (assoc-in [:state :tags] :failure)
             (assoc-in [:data-study-results] nil))
     :dispatch-n [after-fx
                  [:dre.network/default-failure (merge {:event :clinical-studies-tag-distribution} opts)]]})))

(rf/reg-event-fx
 ::success-study-size
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [after-fx ::fetch-fx/response]}]]
  {:db (-> db
           (assoc-in [:state :study-size] :success)
           (assoc-in [:data-study-size] (:body response)))
   :dispatch after-fx}))

(rf/reg-event-fx
 ::failure-study-size
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [after-fx ::fetch-fx/response] :as opts}]]
  (if (aborted-request? response)
    {}
    {:db (-> db
             (assoc-in [:state :study-size] :failure)
             (assoc-in [:data-study-size] nil))
     :dispatch-n [after-fx
                  [:dre.network/default-failure (merge {:event :clinical-studies-study-size} opts)]]})))

(defn- categories->csv
  [i18n-db data results-data]
  (let [lines (->> data
                   categories->rows
                   (map butlast) ; exclude last element because we don't want to include the type in csv export
                   (map (fn [[category cnt]]
                          (str category "," cnt))))
        results-data (filter-map results-data #(#{:study/with-results
                                                  :study/without-results} %))
        results-lines (keep (fn [[k cnt]]
                              (str (capitalize-words (name k)) "," cnt))
                            results-data)
        csv-header (str (i18n-utils/translate i18n-db "Category") ", "
                        (i18n-utils/translate i18n-db "count"))]
    (str/join "\n" (concat [csv-header] lines results-lines))))

(defn- study-size->csv
  [i18n-db data interval]
  (let [lines (->> data
                   :results
                   (map normalize-key)
                   (sort-by first)
                   (map (fn [[size cnt]]
                          (str size " - " (dec (+ size interval)) "," cnt))))
        csv-header (str (i18n-utils/translate i18n-db "Study Size") ", "
                        (i18n-utils/translate i18n-db "Count"))]
    (str/join "\n" (cons csv-header lines))))

(rf/reg-event-fx
 ::export
 [rf/trim-v]
 (fn-traced
  [{:keys [db]} _]
  (let [i18n-db (:i18n db)
        {:keys [data-categories data-study-size study-size-interval data-study-results]}
        (get-in db db-path)]
    {:fx [[:dispatch [::dre.fx/download {:content (categories->csv i18n-db data-categories data-study-results)
                                         :filename "categories.csv"
                                         :mimetype "text/csv"}]]
          [:dispatch [::dre.fx/download {:content (study-size->csv i18n-db data-study-size study-size-interval)
                                         :filename "study-size.csv"
                                         :mimetype "text/csv"}]]]})))

(rf/reg-event-fx
 ::fetch-study-size
 [rf/trim-v]
 (fn-traced
  [{db :db} [interval]]
  (let [search-db (get db search-events/db-path)
        search-query (:search-query search-db)
        widget-filters (:widget-filters search-db)
        aq (partial search-events/analysis-query search-query widget-filters)
        aq* (comp second aq)
        pills-api-params-fn aq*
        params (pills-api-params-fn exclude-ids)
        study-size-int interval]
    {:db (assoc-in db (concat db-path [:state :study-size]) :loading)
     ::network/request-with-abort [{:endpoint :widgets/study-size}
                                   {:body (stringify-keys-with-namespaces (assoc params :interval study-size-int))
                                    :on-success [::success-study-size {}]
                                    :on-failure [::failure-study-size {}]}]})))

(rf/reg-event-fx
 ::set-study-size-interval
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [interval]]
  (let [interval (if (or (not interval)
                         (< interval 1))
                   1
                   interval)]
    {:db (assoc db :study-size-interval interval)
   :dispatch [::fetch-study-size interval]})))

;; -- Subscriptions

(rf/reg-sub
  ::state
  (fn [db _]
    (get-in db db-path)))

(rf/reg-sub
 ::used-in-search?
 selected-data?)

(rf/reg-sub
  ::status
  :<- [::state]
  (fn [db _]
    (cond
      (or (= :failure (:study-size (:state db))) (= :failure (:tags (:state db))) (= :failure (:categories (:state db))))
      :failure
      (or (= :loading (:study-size (:state db))) (= :loading (:tags (:state db))) (= :loading (:categories (:state db))))
      :loading
      :else
      :success)))

(rf/reg-sub
 ::data
 :<- [::state]
 :<- [::search-events/widget-filters]
 (fn [[db widget-filters] _]
   (let [filter-key id
         study-results-data (:data-study-results db)
         study-results-data (prepare-study-results-data study-results-data widget-filters "study" filter-key)
         study-results-tbl (keep
                            identity
                            (concat
                             [(when (seq study-results-data)
                                {:label [:span.separator [tr "Trial Results"]]
                                 :term "divider"
                                 :asis true
                                 :style {:font-weight "bold"}})]
                             (reverse (sort-by :count study-results-data))))
         study-results-tbl (filter :label study-results-tbl)
         categories-data (:data-categories db)
         categories-data (-> categories-data
                             categories->rows
                             rows->groups
                             normalize-groups
                             (->table-data widget-filters filter-key))]
     (concat study-results-tbl categories-data))))

(rf/reg-sub
 ::data-sankey
 :<- [::state]
 (fn [db _]
   (:data-categories-sankey db)))

(rf/reg-sub
 ::data-study-size
 :<- [::state]
 :<- [::search-events/widget-filters]
 (fn [[db widget-filters] _]
   (let [{:keys [interval results]} (:data-study-size db)]
     (->> results
          (map normalize-key)
          (sort-by first)
          (map (partial study-size->table-row widget-filters interval :clinical-studies-size))))))

(rf/reg-sub
 ::study-size-interval
 :<- [::state]
 (fn [db _]
   (:study-size-interval db)))
